<template>
  <div class="relative w-full">
    <div class="border-b border-white/5 w-full h-[75px] flex items-center justify-end px-4 sm:px-14 py-6">
      <div class="flex items-center gap-4">
        <!-- Bot Selector (Existing Code) -->
        <div class="relative">
          <Select v-model="selectedBotId">
            <SelectTrigger class="w-auto bg-white/5 border-white/10 relative min-w-[200px]">
              <div class="flex items-center gap-4 text-white">
                <div v-if="selectedBot">
                  <CrownIcon class="h-5 w-5 text-yellow-500" />
                </div>
                <div v-else class="relative">
                  <CrownIcon class="h-5 w-5 text-emerald-400 relative z-10 animate-pulse-subtle" />
                  <!-- Emerald waves animation when no bot is selected -->
                  <div class="absolute inset-0 z-0">
                    <div class="wave-effect wave-1"></div>
                    <div class="wave-effect wave-2"></div>
                    <div class="wave-effect wave-3"></div>
                  </div>
                </div>
                <span :class="!selectedBot ? 'text-emerald-400 animate-pulse-text' : 'text-white'">
                  {{ selectedBot ? selectedBot.name : 'Selecionar bot' }}
                </span>
              </div>
            </SelectTrigger>
            <SelectContent class="bg-[#2a2a2a] border-white/10">
              <div v-if="bots" class="py-1">
                <SelectItem
                  v-for="bot in selectableBots"
                  :key="bot.id"
                  :value="String(bot.id)"
                  class="text-white hover:bg-white/5"
                >
                  <div class="flex items-center gap-2 text-md">
                    {{ bot.name }}
                  </div>
                </SelectItem>
              </div>
              <div 
                v-else 
                class="flex items-center justify-center p-4"
              >
                <Loader2 class="w-5 h-5 animate-spin" />
              </div>
            </SelectContent>
          </Select>
          
          <!-- Improved floating tooltip below the select -->
          <div 
            v-if="!selectedBot && (bots?.data?.length ?? 0) > 0" 
            class="absolute top-full right-0 transform mt-4 tooltip-container w-full min-w-[350px]"
          >
            <div class="bg-gradient-to-r from-emerald-900/80 to-emerald-700/80 backdrop-blur-sm text-white px-4 py-3 rounded-lg shadow-lg tooltip-bounce">
              <div class="absolute -top-2 left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-[8px] border-r-[8px] border-b-[8px] border-transparent border-b-emerald-800/80"></div>
              <div class="flex items-center gap-2">
                <BotIcon class="h-5 w-5 text-emerald-300" />
                <span class="font-medium">Selecione um bot para continuar</span>
              </div>
              <div class="text-xs text-emerald-200 mt-1 pl-7">Escolha entre os bots disponíveis para iniciar</div>
            </div>
          </div>
        </div>

        <!-- Flow Selector (New Addition) -->
        <Transition
          v-if="selectedBot?.type === BotTypeEnum.CHAT_FLOW"
          enter-active-class="transition duration-300 ease-out"
          enter-from-class="transform -translate-x-4 opacity-0"
          enter-to-class="transform translate-x-0 opacity-100"
          leave-active-class="transition duration-200 ease-in"
          leave-from-class="transform translate-x-0 opacity-100"
          leave-to-class="transform -translate-x-4 opacity-0"
        >
          <div v-if="selectedBot" class="hidden sm:flex items-center gap-3">
            <div class="h-8 w-[1px] bg-white/10"></div>
            
            <div class="relative">
              <div class="flex items-center gap-3">
                <div class="relative">
                  <div v-if="!flows" class="h-9 w-[180px] flex items-center justify-center">
                    <Loader2 class="h-4 w-4 text-emerald-400 animate-spin" />
                  </div>
                  <div v-else class="relative">
                    <Select 
                      :model-value="selectedFlow"
                      @update:model-value="(flowId) => updateFlowStatus(Number(flowId), FlowStatusEnum.ACTIVE)"
                      :disabled="isLoading"
                    >
                      <SelectTrigger 
                      class="w-auto bg-white/5 border-white/10 relative min-w-[200px]"
                      >
                        <!-- <div class="absolute inset-0 bg-emerald-500/10 group-hover:bg-emerald-500/20 transition-colors duration-200"></div> -->
                        <div class="flex items-center gap-2 z-10 relative">
                          <GitBranch class="h-4 w-4 text-emerald-300" />
                          <span class="truncate">
                            {{ selectedFlow ? flows.data.find(f => String(f.id) === selectedFlow)?.name : 'Selecionar fluxo' }}
                          </span>
                        </div>
                      </SelectTrigger>
                      
                      <SelectContent class="bg-[#2a2a2a] border-white/10 max-h-[280px]">
                        <div class="py-1">
                          <SelectItem
                            v-for="flow in flows.data"
                            :key="flow.id"
                            :value="String(flow.id)"
                            class="text-white group"
                          >
                            <div class="flex items-center gap-2 text-md">
                              <span>{{ flow.name }}</span>
                            </div>
                          </SelectItem>
                        </div>
                      </SelectContent>
                    </Select>
                    
                    <!-- Glow effect -->
                    <div 
                      v-if="selectedFlow && !isLoading" 
                      class="absolute inset-0 rounded-md -z-10 glow-effect"
                    ></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Transition>
      </div>

      <!-- Profile Button -->
      <Tooltip>
        <TooltipTrigger asChild>
          <RouterLink
            :to="{ name: 'profile' }"
            class="flex items-center justify-center w-10 h-10 rounded-full bg-white/5 hover:bg-emerald-500/20 border border-white/10 hover:border-emerald-500/30 transition-all duration-200 flex-shrink-0 ml-2"
          >
            <UserIcon class="h-5 w-5 text-zinc-400 group-hover:text-emerald-400" />
          </RouterLink>
        </TooltipTrigger>
        <TooltipContent>
          <p>Meu Perfil</p>
        </TooltipContent>
      </Tooltip>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useBot } from '@/composables/useBot';
import { Bot } from '@/interfaces/Bot';
import { CrownIcon, BotIcon, Loader2, ActivityIcon, GitBranch, UserIcon } from 'lucide-vue-next';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { watch, ref, onMounted } from 'vue';
import { Pagination as IPagination } from '@/interfaces/Pagination';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from '@/components/ui/select'
import { flows, useFlow } from '@/composables/chatFlow/useFlow';
import { Flow } from '@/interfaces/chat-flow/flow/Flow';
import { FlowStatusEnum } from '@/enums/chatFlow/flows/FlowStatusEnum';
import { computed } from '@vue/reactivity';
import { BotTypeEnum } from '@/enums/bots/BotTypeEnum';

const bots = ref<IPagination<Bot>>();
const { selectedBot, getAll, selectBot, selectableBots } = useBot();
const selectedBotId = ref<string>('');
const { getAll: getAllFlows, update } = useFlow();

// Flow state
const isLoading = ref(false);

// Sample flows data - replace with your actual data
const selectedFlow = computed(() => {
  return flows.value?.data.find(f => f.status === FlowStatusEnum.ACTIVE)?.id.toString();
});

const updateFlowStatus = async (flowId: number, status: FlowStatusEnum) => {
  await update(flowId, { status })
  
  if (flows.value) {
    flows.value.data = flows.value.data.map(flow => {
      if (flow.id === flowId) {
        return { ...flow, status };
      } else {
        return { ...flow, status: FlowStatusEnum.INACTIVE };
      }
    });
  }
}

watch(selectedBot, (newBot) => {
  selectedBotId.value = String(newBot?.id ?? '');
});

// Load bots when component mounts
const loadBots = async () => {
  bots.value = await getAll({ perPage: '50' });
  selectableBots.value = bots.value.data;
};

// Watch for selectedBotId changes to update the selected bot
watch(selectedBotId, async (newId) => {
  if (newId && bots.value) {
    const bot = bots.value.data.find(b => String(b.id) === newId);
    if (bot) {
      selectBot(bot);
    }
  }
});

onMounted(() => {
  // Load flows when component mounts
  getAllFlows({ perPage: '50' }).then(fetchedFlows => {
    flows.value = fetchedFlows;
  });
});
// Initialize bots data
loadBots();
</script>

<style scoped>
.wave-effect {
  position: absolute;
  border-radius: 50%;
  background-color: rgba(16, 185, 129, 0.3);
  width: 100%;
  height: 100%;
  opacity: 0;
  transform: scale(1);
  animation: pulse 2.5s infinite;
}

.wave-2 {
  animation-delay: 0.8s;
}

.wave-3 {
  animation-delay: 1.6s;
}

@keyframes pulse {
  0% {
    transform: scale(0.8);
    opacity: 0.8;
  }
  70% {
    transform: scale(2.2);
    opacity: 0;
  }
  100% {
    transform: scale(2.2);
    opacity: 0;
  }
}

@keyframes pulse-subtle {
  0%, 100% {
    opacity: 1;
    filter: drop-shadow(0 0 8px rgba(16, 185, 129, 0.8));
  }
  50% {
    opacity: 0.8;
    filter: drop-shadow(0 0 12px rgba(16, 185, 129, 0.6));
  }
}

.animate-pulse-subtle {
  animation: pulse-subtle 2.5s ease-in-out infinite;
}

@keyframes pulse-text {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.7;
  }
}

.animate-pulse-text {
  animation: pulse-text 2.5s ease-in-out infinite;
}

.tooltip-container {
  z-index: 50;
  filter: drop-shadow(0 4px 12px rgba(16, 185, 129, 0.4));
}

.tooltip-bounce {
  animation: tooltip-bounce 3s ease-in-out infinite;
}

@keyframes tooltip-bounce {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-5px);
  }
}

/* New styles for the flow selector */
.glow-effect {
  animation: glow 2s ease-in-out infinite;
}

@keyframes glow {
  0%, 100% {
    box-shadow: 0 0 0px rgba(16, 185, 129, 0.2);
  }
  50% {
    box-shadow: 0 0 15px rgba(16, 185, 129, 0.6);
  }
}

.status-bounce {
  animation: status-bounce 3s ease-in-out infinite;
}

@keyframes status-bounce {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(3px);
  }
}

/* Particle animations for the select trigger */
.particle {
  position: absolute;
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.6);
  pointer-events: none;
}

.particle:nth-child(1) {
  top: 20%;
  left: 10%;
  animation: float 4s ease-in-out infinite;
}

.particle:nth-child(2) {
  top: 60%;
  left: 20%;
  animation: float 6s ease-in-out infinite 1s;
}

.particle:nth-child(3) {
  top: 40%;
  left: 50%;
  animation: float 5s ease-in-out infinite 0.5s;
}

.particle:nth-child(4) {
  top: 10%;
  left: 70%;
  animation: float 7s ease-in-out infinite 2s;
}

.particle:nth-child(5) {
  top: 70%;
  left: 85%;
  animation: float 5s ease-in-out infinite 1.5s;
}

@keyframes float {
  0%, 100% {
    transform: translateY(0) translateX(0);
  }
  25% {
    transform: translateY(-10px) translateX(5px);
  }
  50% {
    transform: translateY(0) translateX(10px);
  }
  75% {
    transform: translateY(10px) translateX(5px);
  }
}
</style>
